import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors, fontSize, margins } from "style";
import Modal from "components/Modal";
import Form from "components/Form";

/**
 * From wrapped in a Modal
 *
 * @param {Element}  title
 * @param {Element}  subTitle
 * @param {Element}  description
 * @param {Element}  info
 * @param {Boolean}  show
 * @param {Function} onClose
 * @param {Function} onDestructive
 * @param {Function} onCompleted
 * @param {String}   size
 * @param {Boolean}  isDestructable
 * @param {String}   mutationType
 * @param {Boolean}  loading
 * @param {String}   primaryButtonMessageId
 * @param {String}   secondaryButtonMessageId
 * @param {Node}     children
 * @param {Boolean}  showSave
 * @param {Boolean}  showCancel
 * @param {Function} onError
 * @param {Boolean}  disabled
 * @param {Node}     disclaimer
 * @param {Node}     aside      Side panel beside the form and footer, scrolling on its own
 * @param {String}   marginSize
 * @param {Object}   modalProps Props forwarded to the underlying modal
 * @param {Boolean}  canSubmit   Whether the primary action is available, on top of the form's required values
 * @param {Boolean}  allowPartialSave Save submits through submitPartial, so required fields can stay empty
 */
const ModalForm = ({
  title,
  subTitle,
  description,
  info,
  show,
  onClose,
  closeOnComplete = true,
  onDestructive,
  onCompleted,
  onError,
  size,
  isDestructable,
  mutationType,
  loading: loadingProp,
  primaryButtonMessageId,
  secondaryButtonMessageId,
  footer,
  children,
  showSave,
  showCancel,
  disabled,
  disclaimer,
  aside,
  marginSize,
  canSubmit: canSubmitProp = true,
  modalProps,
  allowPartialSave = false,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const childForm = useRef(null);
  const submitPartial = (overrides) => childForm.current?.submitPartial(overrides);
  const handleSubmit = () => (allowPartialSave ? submitPartial() : childForm.current.triggerSubmitForm());

  const handleError = (error) => {
    setLoading(false);

    if (typeof onError === "function") onError(error);
  };

  const handleComplete = (payload) => {
    if (closeOnComplete && typeof onClose === "function") onClose();
    if (typeof onCompleted === "function") onCompleted(payload);
  };

  const handleClose = () => {
    onClose();
  };

  // FE tests are failing by keeping margins.normal as default prop
  const FormDescription = () =>
    description ? <div css={styles.description(marginSize || margins.normal)}>{description}</div> : null;

  return (
    <Modal
      {...modalProps}
      title={title}
      subTitle={subTitle}
      show={show}
      isDestructable={isDestructable}
      onDestructive={onDestructive}
      onClose={handleClose}
      onSubmit={handleSubmit}
      loading={loading || loadingProp}
      canSubmit={(allowPartialSave || canSubmit) && canSubmitProp}
      size={size}
      primaryButtonMessageId={primaryButtonMessageId}
      secondaryButtonMessageId={secondaryButtonMessageId}
      footer={typeof footer === "function" ? footer({ submitPartial, loading }) : footer}
      showSave={showSave}
      showCancel={showCancel}
      disabled={disabled}
      info={info}
      aside={aside}
    >
      <>
        <FormDescription />
        {children}
        <Form
          ref={childForm}
          onCompleted={handleComplete}
          onError={handleError}
          onLoadingState={setLoading}
          mutationType={mutationType}
          onRequiredValuesChange={setCanSubmit}
          disabled={disabled}
          noPadding
          {...props}
        />
        {disclaimer}
      </>
    </Modal>
  );
};

const styles = {
  description: (marginSize) => css`
    font-size: ${fontSize.small};
    line-height: 2.4rem;
    color: ${colors.grayAnatomyBase};
    margin-bottom: ${marginSize};
  `,
};

ModalForm.propTypes = {
  title: PropTypes.any,
  subTitle: PropTypes.string,
  description: PropTypes.any,
  info: PropTypes.any,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  closeOnComplete: PropTypes.bool,
  onDestructive: PropTypes.func,
  onCompleted: PropTypes.func,
  size: PropTypes.string,
  isDestructable: PropTypes.bool,
  mutationType: PropTypes.string,
  loading: PropTypes.bool, // loadingProp
  primaryButtonMessageId: PropTypes.string,
  secondaryButtonMessageId: PropTypes.string,
  children: PropTypes.node,
  showSave: PropTypes.bool,
  showCancel: PropTypes.bool,
  onError: PropTypes.func,
  footer: PropTypes.any,
  disabled: PropTypes.bool,
  disclaimer: PropTypes.node,
  aside: PropTypes.node,
  marginSize: PropTypes.string,
  canSubmit: PropTypes.bool,
  modalProps: PropTypes.object,
  allowPartialSave: PropTypes.bool,
};

export default ModalForm;
